import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { UploadCloud, Trash2, RefreshCw, ExternalLink, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { CONFERENCE } from '../../mock';

const BASE = process.env.REACT_APP_BACKEND_URL;
const LEVELS = { sponsor: CONFERENCE.sponsorships.map((s) => s.tier), ad: CONFERENCE.journal.map((j) => j.type) };
const inputCls = 'w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none';
const EMPTY = { name: '', kind: 'sponsor', level: LEVELS.sponsor[0], link: '' };

function Label({ children, required }) {
  return <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">{children}{required && <span className="text-red-400"> *</span>}</span>;
}

export const AdminDigitalJournal = ({ password }) => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);
  const headers = { 'X-Admin-Password': password };

  const load = () => axios.get(`${BASE}/api/digital-journal`).then((r) => setItems(r.data.items || [])).catch(() => toast.error('Could not load journal.'));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!form.name.trim()) { toast.error('Enter the sponsor or advertiser name.'); return; }
    if (!file) { toast.error('Pick the ad image first.'); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append('file', file);
    setUploading(true);
    try {
      await axios.post(`${BASE}/api/admin/digital-journal/upload`, fd, { headers });
      toast.success('Added to the Digital Ad Journal.');
      setForm({ ...EMPTY, kind: form.kind, level: form.level });
      setFile(null);
      document.getElementById('journal-file-input').value = '';
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.name}" from the Digital Ad Journal?`)) return;
    try {
      await axios.delete(`${BASE}/api/admin/digital-journal/${it.id}`, { headers });
      toast.success('Removed.');
      load();
    } catch {
      toast.error('Could not remove.');
    }
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6">
      <div className="card-dark rounded-md p-5 h-fit space-y-3" data-testid="journal-uploader">
        <div className="flex items-center gap-2 mb-1"><UploadCloud className="text-gold" size={18} /><h3 className="font-heading tracking-widest text-gold text-sm">ADD SPONSOR / AD</h3></div>
        <label className="block"><Label required>SPONSOR / ADVERTISER NAME</Label>
          <input data-testid="journal-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block"><Label required>TYPE</Label>
            <select data-testid="journal-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, level: LEVELS[e.target.value][0] })} className={inputCls}>
              <option value="sponsor">Sponsor</option>
              <option value="ad">Journal Ad</option>
            </select>
          </label>
          <label className="block"><Label required>{form.kind === 'sponsor' ? 'TIER' : 'AD SIZE'}</Label>
            <select data-testid="journal-level-select" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className={inputCls}>
              {LEVELS[form.kind].map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        </div>
        <label className="block"><Label required>AD IMAGE / LOGO</Label>
          <input id="journal-file-input" data-testid="journal-file-input" type="file" accept="image/*,.heic,.heif" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-xs text-cbtu-cream file:bg-gold file:text-cbtu-black file:border-0 file:rounded-sm file:px-3 file:py-2 file:mr-3 file:font-heading file:text-xs file:tracking-widest file:cursor-pointer" />
          <span className="text-[11px] text-cbtu-muted block mt-1">JPG, PNG or iPhone HEIC. Max 15 MB. Have a PDF ad? Save it as an image first.</span>
        </label>
        <label className="block"><Label>WEBSITE LINK (OPTIONAL)</Label>
          <input data-testid="journal-link-input" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="e.g. www.ila1408.org" className={inputCls} />
        </label>
        <button onClick={upload} disabled={uploading} data-testid="journal-upload-btn" className="w-full bg-[#2ca01c] hover:bg-[#248a16] disabled:opacity-60 text-white font-heading text-xs tracking-widest px-4 py-3 rounded-sm inline-flex items-center justify-center gap-2 transition-colors">
          <UploadCloud size={14} /> {uploading ? 'UPLOADING…' : 'ADD TO JOURNAL'}
        </button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3 text-xs">
          <span className="text-gold font-heading tracking-widest" data-testid="journal-count">{items.length} listing{items.length === 1 ? '' : 's'}</span>
          <div className="flex items-center gap-3">
            <a href="/digital-journal?preview=1#flipbook" className="text-orange-200 hover:text-gold inline-flex items-center gap-1 text-[11px]" data-testid="journal-admin-preview-link"><BookOpen size={11} /> PREVIEW FLIPBOOK</a>
            <a href="/digital-journal" target="_blank" rel="noreferrer" className="text-gold hover:text-gold-bright inline-flex items-center gap-1 text-[11px]"><BookOpen size={11} /> VIEW PUBLIC PAGE</a>
            <button onClick={load} className="text-cbtu-muted hover:text-gold inline-flex items-center gap-1 text-[11px]"><RefreshCw size={11} /> REFRESH</button>
          </div>
        </div>
        {items.length === 0 && <div className="card-dark rounded-md p-10 text-center text-cbtu-muted">No listings yet. Use the form on the left to add the first sponsor or ad.</div>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <figure key={it.id} className="card-dark rounded-md overflow-hidden flex flex-col" data-testid={`admin-journal-tile-${it.id}`}>
              <img src={`${BASE}${it.src}`} alt={it.name} className="w-full aspect-[4/5] object-contain bg-white/5" loading="lazy" />
              <figcaption className="p-3 border-t border-gold/20 flex items-start justify-between gap-2">
                <div>
                  <div className="text-cbtu-cream text-sm">{it.name}</div>
                  <div className="text-[10px] font-heading tracking-widest text-gold mt-0.5">{it.level.toUpperCase()}</div>
                  {it.link && <a href={it.link} target="_blank" rel="noreferrer" className="text-[11px] text-cbtu-muted hover:text-gold inline-flex items-center gap-1 mt-1">{it.link.replace(/^https?:\/\//, '')} <ExternalLink size={10} /></a>}
                </div>
                <button onClick={() => remove(it)} data-testid={`admin-journal-delete-${it.id}`} className="p-1 rounded-sm text-red-400 hover:bg-red-500/20" aria-label="Remove"><Trash2 size={13} /></button>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
};
